"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT, LABEL } from "@/components/tool-kit/styles";
import { AnonymizerResults } from "@/components/tools/qualitative-text/anonymizer-results";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type AnonymizeOptions,
  anonymize,
  type DateMode,
  type PseudonymStyle,
} from "@/lib/tool-kit/qualitative-text/anonymize";
import {
  type AnonCategory,
  LIST_CATEGORIES,
  type ListCategory,
} from "@/lib/tool-kit/qualitative-text/anonymize-names";
import {
  PATTERN_CATEGORIES,
  type PatternCategory,
} from "@/lib/tool-kit/qualitative-text/anonymize-patterns";

/** English strings. A translated content file overrides these through `ui`. */
export const TEXT_ANONYMIZER_STRINGS = {
  privacy:
    "Runs entirely in your browser. Your transcript, your lists and your key file are never uploaded or saved.",
  textLabel: "Transcript or text to anonymize",
  textPlaceholder:
    "Paste an interview transcript, focus-group notes, open-ended survey answers or any text that names people.",
  trySample: "Try a sample",
  clear: "Clear",
  listsHeading: "Names, places and terms to replace",
  listsHint:
    "One per line. Put spellings of the same thing on one line, separated by | (Sarah Johnson | Sarah | Ms Johnson). Add => to choose the pseudonym (Sarah => Alice). Lines starting with # are ignored.",
  listTitles: {
    person: "People",
    place: "Places",
    organisation: "Organisations",
    other: "Other terms",
  } as Record<ListCategory, string>,
  listPlaceholders: {
    person: "Priya Nair | Priya",
    place: "Leeds",
    organisation: "Northfield Hospital",
    other: "Project Kestrel",
  } as Record<ListCategory, string>,
  detectHeading: "Detect automatically",
  detectLabels: {
    email: "Email addresses",
    phone: "Phone numbers",
    url: "Web addresses",
    handle: "@handles",
    ip: "IP addresses",
    postcode: "Postcodes and ZIP codes",
    id: "ID numbers (SSN, NINO, card)",
    date: "Dates",
  } as Record<PatternCategory, string>,
  hintsHeading: "Find names from hints in the text",
  speakers: "Speaker labels (Priya Nair: …) and Name: fields",
  titles: "Titled names (Dr Okafor, Ms Lee)",
  intros: "Introductions (My name is …)",
  optionsHeading: "Matching",
  nameParts: "Also match first names and surnames on their own",
  matchCase: "Match capitalization exactly",
  styleLabel: "Pseudonym style",
  styles: {
    labels: "Person 1, Place 1",
    tags: "[PERSON_1], [PLACE_1]",
    category: "[PERSON], [PLACE] (no numbering, no key)",
  } as Record<PseudonymStyle, string>,
  dateLabel: "Dates become",
  dateModes: {
    tag: "A label (Date 1)",
    year: "The year only",
  } as Record<DateMode, string>,
  keyInputLabel: "Key file from an earlier transcript (optional)",
  keyInputHint:
    "Paste the CSV you downloaded before and the same people keep the same pseudonyms across transcripts.",
  categories: {
    person: "Person",
    place: "Place",
    organisation: "Organisation",
    other: "Term",
    email: "Email",
    phone: "Phone",
    url: "URL",
    handle: "Handle",
    ip: "IP address",
    postcode: "Postcode",
    id: "ID number",
    date: "Date",
  } as Record<AnonCategory, string>,
  emptyPrompt: "Paste a transcript to anonymize it.",
  results: "Anonymized text",
  reviewWarning:
    "This is an aid, not a guarantee. Pattern matching misses identifiers it was not told about and cannot judge which details identify someone in context. Read the whole output, and have a person check it, before you share or publish it.",
  statReplaced: "Replacements",
  statDistinct: "Distinct pseudonyms",
  statCandidates: "Words to review",
  outputLabel: "Anonymized text",
  copyOutput: "Copy text",
  copied: "Copied!",
  downloadOutput: "Download .txt",
  outputFilename: "anonymized-text.txt",
  showHighlights: "Show what was replaced, in your original text",
  highlightHint:
    "Hover over a highlight to see the original and its pseudonym.",
  highlightLabel: "Your original text with every replaced span highlighted",
  keyHeading: "Key file",
  keyHint:
    "This table links every pseudonym to the real value. It identifies your participants: save it separately from the anonymized text and keep it under the same access controls as your raw data.",
  keyEmpty:
    "No key: nothing was replaced, or the category-only style was chosen, which cannot be reversed.",
  colCategory: "Category",
  colOriginal: "Original",
  colPseudonym: "Pseudonym",
  colCount: "Uses",
  downloadKey: "Download key (CSV)",
  copyKey: "Copy key",
  keyFilename: "anonymization-key.csv",
  ambiguous:
    "Not matched on their own because they belong to more than one person: {names}. List them on a line of their own if you want them replaced.",
  candidatesHeading: "Capitalized words that were not replaced",
  candidatesHint:
    "Names, places and employers often hide here. Add the ones that identify someone and the output updates.",
  candidatesNone: "No other capitalized words in mid-sentence.",
  candidateCount: "one:seen {count} time|other:seen {count} times",
  addTo: "Add to {list}",
  sample:
    "Interviewer: Thanks for meeting me. Could you tell me about your role?\nPriya Nair: Of course. I'm a ward manager at Northfield Hospital in Leeds. I started on 4 March 2019, after moving up from Sheffield.\nInterviewer: What was the first year like?\nPriya Nair: Hard. Dr. Okafor ran the ward then, and he rang me at 0113 496 0123 most evenings. You can email me at priya.nair@northfield.example if you want the rota.",
  sampleLists: {
    person: "",
    place: "Leeds\nSheffield",
    organisation: "Northfield Hospital",
    other: "",
  } as Record<ListCategory, string>,
} as const;

const EMPTY_LISTS: Record<ListCategory, string> = {
  person: "",
  place: "",
  organisation: "",
  other: "",
};

const DEFAULT_DETECT: Record<PatternCategory, boolean> = {
  email: true,
  phone: true,
  url: true,
  handle: true,
  ip: true,
  postcode: true,
  id: true,
  date: true,
};

export default function TextAnonymizerWidget() {
  const strings = useToolUi(TEXT_ANONYMIZER_STRINGS);
  const [text, setText] = useState("");
  const [lists, setLists] = useState(EMPTY_LISTS);
  const [detect, setDetect] = useState(DEFAULT_DETECT);
  const [speakers, setSpeakers] = useState(true);
  const [titles, setTitles] = useState(true);
  const [intros, setIntros] = useState(true);
  const [nameParts, setNameParts] = useState(true);
  const [matchCase, setMatchCase] = useState(false);
  const [style, setStyle] = useState<PseudonymStyle>("labels");
  const [dateMode, setDateMode] = useState<DateMode>("tag");
  const [existingKey, setExistingKey] = useState("");
  const deferredText = useDeferredValue(text);
  const deferredLists = useDeferredValue(lists);
  const deferredKey = useDeferredValue(existingKey);

  const options: AnonymizeOptions = useMemo(
    () => ({
      lists: deferredLists,
      detect,
      speakers,
      titles,
      intros,
      nameParts,
      matchCase,
      style,
      dateMode,
      labels: strings.categories,
      existingKey: deferredKey,
    }),
    [
      deferredLists,
      detect,
      speakers,
      titles,
      intros,
      nameParts,
      matchCase,
      style,
      dateMode,
      strings.categories,
      deferredKey,
    ]
  );
  const result = useMemo(
    () => (deferredText.trim() ? anonymize(deferredText, options) : null),
    [deferredText, options]
  );

  function addTerm(category: ListCategory, term: string) {
    setLists((current) => ({
      ...current,
      [category]: current[category] ? `${current[category]}\n${term}` : term,
    }));
  }

  return (
    <>
      <Notice>{strings.privacy}</Notice>
      <TextAreaField
        id="anon-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sample);
            setLists(strings.sampleLists);
          }}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <fieldset className="space-y-3">
        <legend className={LABEL}>{strings.listsHeading}</legend>
        <p className={HINT}>{strings.listsHint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {LIST_CATEGORIES.map((category) => (
            <TextAreaField
              id={`anon-list-${category}`}
              key={category}
              label={strings.listTitles[category]}
              onChange={(value) =>
                setLists((current) => ({ ...current, [category]: value }))
              }
              placeholder={strings.listPlaceholders[category]}
              rows={4}
              value={lists[category]}
            />
          ))}
        </div>
      </fieldset>
      <div className="grid gap-6 sm:grid-cols-2">
        <fieldset className="space-y-2">
          <legend className={LABEL}>{strings.detectHeading}</legend>
          {PATTERN_CATEGORIES.map((category) => (
            <Checkbox
              checked={detect[category]}
              key={category}
              label={strings.detectLabels[category]}
              onChange={(checked) =>
                setDetect((current) => ({ ...current, [category]: checked }))
              }
            />
          ))}
        </fieldset>
        <div className="space-y-5">
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.hintsHeading}</legend>
            <Checkbox
              checked={speakers}
              label={strings.speakers}
              onChange={setSpeakers}
            />
            <Checkbox
              checked={titles}
              label={strings.titles}
              onChange={setTitles}
            />
            <Checkbox
              checked={intros}
              label={strings.intros}
              onChange={setIntros}
            />
          </fieldset>
          <fieldset className="space-y-2">
            <legend className={LABEL}>{strings.optionsHeading}</legend>
            <Checkbox
              checked={nameParts}
              label={strings.nameParts}
              onChange={setNameParts}
            />
            <Checkbox
              checked={matchCase}
              label={strings.matchCase}
              onChange={setMatchCase}
            />
          </fieldset>
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <PillGroup
          label={strings.styleLabel}
          name="anon-style"
          onChange={setStyle}
          options={(Object.keys(strings.styles) as PseudonymStyle[]).map(
            (value) => ({
              value,
              label: strings.styles[value],
            })
          )}
          value={style}
        />
        <SelectField
          id="anon-date-mode"
          label={strings.dateLabel}
          onChange={setDateMode}
          options={(Object.keys(strings.dateModes) as DateMode[]).map(
            (value) => ({
              value,
              label: strings.dateModes[value],
            })
          )}
          value={dateMode}
        />
      </div>
      <TextAreaField
        hint={strings.keyInputHint}
        id="anon-key-input"
        label={strings.keyInputLabel}
        onChange={setExistingKey}
        rows={3}
        value={existingKey}
      />
      {result ? (
        <AnonymizerResults
          onAddTerm={addTerm}
          result={result}
          strings={strings}
          text={deferredText}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
